/*
 * Copyright (c) 2020-2024. World Wide Generation Ltd
 */

import { RouteInterface, RoutesInterface } from '@g17eco/types/routes';
import { InitiativePermissions } from '@services/permissions/InitiativePermissions';
import { CompanySettingsPages } from '../utils';
import { PACK } from '@constants/terminology';

export const materialityTrackerRedirect: RoutesInterface = {
  MATERIALITY_TRACKER_REDIRECT: {
    id: 'materiality_tracker_assessment_list_redirect',
    label: 'Assessment List Redirect',
    path: '/materiality-tracker/:initiativeId/redirect/:destination(assessment-list)',
    defaultParams: {
      destination: 'assessment-list',
    },
    auth: true,
  },
};

export const materialityAssessmentInitiativeRoutes: RoutesInterface = {
  MATERIALITY_TRACKER_COMPANY_SETTINGS: {
    id: 'materiality_tracker_company_settings',
    label: 'Company Settings',
    path: '/materiality-tracker/:initiativeId/admin/company-settings/:page?',
    defaultParams: { page: CompanySettingsPages.Details },
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_MANAGE_USERS: {
    id: 'materiality_tracker_manage_users',
    label: 'Manage Users',
    path: '/materiality-tracker/:initiativeId/admin/manage-users/:page?',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_ADMIN: {
    id: 'materialityAssessment_admin',
    label: 'Admin',
    path: '/materiality-tracker/:initiativeId/admin',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_INSIGHTS: {
    id: 'materialityAssessment_insights',
    label: 'Insights',
    path: '/materiality-tracker/:initiativeId/insights/:insightSurveyId',
    auth: true,
    requiredUserRoles: InitiativePermissions.canAccessInsightsAndDownloadsRoles,
    disabledTooltip: 'Restricted users cannot view assessment insights. Please contact your administrator.',
  },
  MATERIALITY_TRACKER_MODULES: {
    id: 'materialityAssessment_modules',
    label: PACK.CAPITALIZED_PLURAL,
    path: '/materiality-tracker/:initiativeId/custom-metrics/:groupId?/:viewMode?',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_LIST: {
    id: 'materialityAssessment_surveys_list',
    label: 'All assessments',
    path: '/materiality-tracker/:initiativeId/assessment-list',
    exact: true,
    auth: true,
  },
  MATERIALITY_ASSESSMENT_CREATE: {
    id: 'materialityAssessment_surveys_create',
    label: 'All assessments',
    path: '/materiality-tracker/:initiativeId/assessment-list/create',
    exact: true,
    auth: true,
  },
  MATERIALITY_TRACKER_SURVEY: {
    id: 'materialityAssessment_survey',
    label: 'Latest assessment',
    path: '/materiality-tracker/:initiativeId/assessment/:surveyId?',
    exact: true,
    auth: true,
  },
  MATERIALITY_TRACKER_BANKING_SETTINGS: {
    id: 'materiality_tracker_banking_settings',
    label: 'Banking Settings',
    path: '/materiality-tracker/:initiativeId/banking-settings',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_STOCK_EXCHANGE_SETTINGS: {
    id: 'materiality_tracker_stock_exchange_settings',
    label: 'Stock Exchange Settings',
    path: '/materiality-tracker/:initiativeId/stock-exchange-settings',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MATERIALITY_TRACKER_INTEGRATIONS: {
    id: 'materiality_tracker_integrations',
    label: 'Integrations',
    path: '/materiality-tracker/:initiativeId/integrations',
    auth: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
};

export const materialityTrackerRouteEntry: RouteInterface = {
  id: 'materiality_tracker_entry',
  label: 'Materiality Tracker',
  path: '/materiality-tracker/:initiativeId?',
  icon: 'fa-folder-open',
  exact: false,
  auth: true,
};

export const materialityTrackerRoutes: RoutesInterface = {
  MATERIALITY_TRACKER: materialityTrackerRouteEntry,
  ...materialityAssessmentInitiativeRoutes,
};
